import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface PromoCardProps {
  id?: string;
  badge?: {
    text: string;
    icon?: LucideIcon;
    variant?: 'blue' | 'purple' | 'emerald' | 'amber';
  };
  priceBadge?: string;
  title: string;
  description: string;
  actionText: string;
  actionIcon?: LucideIcon;
  onAction: () => void;
  className?: string;
  secondaryAction?: {
    text: string;
    onClick: () => void;
  };
}

export const PromoCard: React.FC<PromoCardProps> = ({
  id,
  badge,
  priceBadge,
  title,
  description,
  actionText,
  actionIcon: ActionIcon,
  onAction,
  className = '',
  secondaryAction,
}) => {
  const BadgeIcon = badge?.icon;

  return (
    <div
      id={id}
      className={`dark-card bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl border border-blue-900/40 relative overflow-hidden ${className}`}
      style={{
        color: '#ffffff',
        // Guarantee heading and text contrast even in nested shells
        ['--customer-heading' as any]: '#ffffff',
        ['--customer-text' as any]: '#f8fafc',
        ['--customer-text-secondary' as any]: '#cbd5e1',
        ['--customer-text-muted' as any]: '#94a3b8',
      }}
    >
      <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-3 max-w-2xl">
          {(badge || priceBadge) && (
            <div className="flex flex-wrap items-center gap-2">
              {badge && (
                <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/40 text-xs font-bold flex items-center gap-1.5">
                  {BadgeIcon && <BadgeIcon className="w-3.5 h-3.5 text-blue-300" />}
                  <span>{badge.text}</span>
                </span>
              )}
              {priceBadge && (
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold">
                  {priceBadge}
                </span>
              )}
            </div>
          )}

          <h3
            className="text-xl md:text-2xl font-black tracking-tight text-white leading-tight"
            style={{ color: '#ffffff' }}
          >
            {title}
          </h3>

          <p
            className="text-xs md:text-sm text-slate-300 leading-relaxed font-normal"
            style={{ color: '#cbd5e1' }}
          >
            {description}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="px-5 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-semibold text-xs md:text-sm transition flex items-center gap-2 border border-white/15 cursor-pointer"
            >
              <span>{secondaryAction.text}</span>
            </button>
          )}

          <button
            type="button"
            onClick={onAction}
            className="px-6 py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs md:text-sm transition flex items-center gap-2 shadow-lg shadow-blue-600/30 cursor-pointer"
          >
            <span>{actionText}</span>
            {ActionIcon && <ActionIcon className="w-4 h-4 text-white" />}
          </button>
        </div>
      </div>
    </div>
  );
};
